import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Link as MuiLink, Stack, TextField, Typography } from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import React from "react";
import CopyToClipboardButton from "../../../common/components/CopyToClipboardButton";
import { PropertyDisplayFormat } from "../../Models/PropertyDisplayFormat";
import { PropertyDisplayModel } from "../../Models/PropertyDisplayModel";
import formatPropertyValue from "./formatPropertyValue";

type DisplayPropertyValueProps = {
  data: any;
  property: PropertyDisplayModel;
  isRowDirection: boolean;
  isEditing: boolean;
  editValue: string | Dayjs | number | null;
  hasChanged: boolean;
  isUpdating: boolean;
  hideSaveCancel?: boolean;
  onEditChange: (value: string | Dayjs | number | null) => void;
  onSave: () => void;
  onCancel: () => void;
  onEnterEditMode: () => void;
  observedPaths?: string[];
};

const DisplayPropertyValue: React.FC<DisplayPropertyValueProps> = ({
  data,
  property,
  isRowDirection,
  isEditing,
  editValue,
  hasChanged,
  isUpdating,
  hideSaveCancel,
  onEditChange,
  onSave,
  onCancel,
  onEnterEditMode,
  observedPaths,
}) => {
  const value = formatPropertyValue(data, property, observedPaths);
  const isEditable = !!property.UpdateData;

  const textSx = {
    fontWeight: "bold",
    flex: 1,
    overflow: isRowDirection ? "hidden" : "epllipsis",
    textOverflow: isRowDirection ? "ellipsis" : undefined,
    textDecoration: "none",
    cursor: isEditable ? "pointer" : "default",
  };

  if (isEditing) {
    const isDateFormat = property.Format === PropertyDisplayFormat.Date;
    const isDateTimeFormat = property.Format === PropertyDisplayFormat.DateTime;
    const isCurrencyFormat = property.Format === PropertyDisplayFormat.Currency;

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !isUpdating) {
        onSave();
      } else if (e.key === "Escape" && !isUpdating) {
        onCancel();
      }
    };

    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        {isDateFormat ? (
          <DatePicker
            value={editValue as Dayjs | null}
            onChange={(newValue: Dayjs | null) => onEditChange(newValue)}
            disabled={isUpdating}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                autoFocus: true,
              },
            }}
            sx={{ minWidth: 0, width: "180px", flex: 1 }}
          />
        ) : isDateTimeFormat ? (
          <DateTimePicker
            value={editValue as Dayjs | null}
            onChange={(newValue: Dayjs | null) => onEditChange(newValue)}
            disabled={isUpdating}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                autoFocus: true,
              },
            }}
            sx={{ minWidth: 0, width: "180px", flex: 1 }}
          />
        ) : isCurrencyFormat ? (
          <TextField
            value={editValue as number | string}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string, numbers, and decimal point
              if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                onEditChange(value === "" ? "" : value);
              }
            }}
            type="text"
            inputMode="decimal"
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <TextField
            value={editValue as string}
            onChange={(e) => onEditChange(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            disabled={isUpdating}
            sx={{ flex: 1, minWidth: 0 }}
            onKeyDown={handleKeyDown}
          />
        )}
        {!hideSaveCancel && (
          <>
            <IconButton size="small" onClick={onSave} color="primary" disabled={!hasChanged || isUpdating} loading={isUpdating}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancel} disabled={isUpdating}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 0.5, flex: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "ellipsis" }} onClick={isEditable ? onEnterEditMode : undefined}>
        {property.Format === PropertyDisplayFormat.EmailLink ? (
          <MuiLink href={`mailto:${value}`} variant="body2" sx={textSx}>
            {value}
          </MuiLink>
        ) : property.Format === PropertyDisplayFormat.PhoneLink ? (
          <MuiLink href={`tel:${value}`} variant="body2" sx={textSx}>
            {value}
          </MuiLink>
        ) : (
          <Typography variant="body2" sx={textSx}>
            {value}
          </Typography>
        )}
      </Box>
      {isEditable && (
        <IconButton size="small" onClick={onEnterEditMode}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      <CopyToClipboardButton value={value} />
    </Box>
  );
};

export default DisplayPropertyValue;
